/** Pages (Effectiveness): analytics are computed client-side from raw session data. */

import { AggregatedSessionV2 } from "@global/types/session";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useMemo, useState } from "react";
import { SingleSegmentSelector } from "../components/segment-selector";
import { useDataStore, useFilters } from "../context";
import {
  CTADefinition,
  DEFAULT_SEGMENTS,
  PageDetailMetrics,
  PageMetrics,
  Segment,
} from "../types/analytics";
import {
  getClickedItems,
  getPageFlow,
  getTimeInView,
  getTimeOnPages,
} from "../utils/session-adapter";

// TYPES

type SortKey = keyof Pick<
  PageMetrics,
  | "entrances"
  | "sessions"
  | "pageviews"
  | "medianTimeOnPage"
  | "exitRate"
  | "samePageCTARate"
  | "assistRate"
>;

type SortDirection = "asc" | "desc";

// HELPER FUNCTIONS

function formatDuration(seconds: number): string {
  if (seconds >= 60) {
    return `${(seconds / 60).toFixed(1)}m`;
  }
  return `${seconds.toFixed(0)}s`;
}

function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function median(arr: number[]): number {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

function filterSessionsBySegment(
  sessions: AggregatedSessionV2[],
  segment: Segment
): AggregatedSessionV2[] {
  if (segment.filters.length === 0) {
    return sessions;
  }

  return sessions.filter((session) => {
    return segment.filters.every((filter) => {
      let value: string | undefined;
      const pageFlow = getPageFlow(session.pageSessions || []);

      switch (filter.type) {
        case "country":
          value = session.country;
          break;
        case "region":
          value = session.region;
          break;
        case "city":
          value = session.city;
          break;
        case "persona":
          value = session.persona || undefined;
          break;
        case "device":
          if (session.screenWidth) {
            if (session.screenWidth < 768) value = "mobile";
            else if (session.screenWidth < 1024) value = "tablet";
            else value = "desktop";
          }
          break;
        case "landing_page":
          value = pageFlow[0];
          break;
        case "referrer":
        case "source":
          value = session.referrer;
          break;
        default:
          return true;
      }

      if (!value) return filter.operator.includes("not");

      const filterValue = Array.isArray(filter.value)
        ? filter.value
        : [filter.value];

      switch (filter.operator) {
        case "equals":
          return filterValue.some(
            (v) => v.toLowerCase() === value!.toLowerCase()
          );
        case "not_equals":
          return filterValue.every(
            (v) => v.toLowerCase() !== value!.toLowerCase()
          );
        case "contains":
          return filterValue.some((v) =>
            value!.toLowerCase().includes(v.toLowerCase())
          );
        case "not_contains":
          return filterValue.every(
            (v) => !value!.toLowerCase().includes(v.toLowerCase())
          );
        case "starts_with":
          return filterValue.some((v) =>
            value!.toLowerCase().startsWith(v.toLowerCase())
          );
        case "ends_with":
          return filterValue.some((v) =>
            value!.toLowerCase().endsWith(v.toLowerCase())
          );
        case "in":
          return filterValue.some(
            (v) => v.toLowerCase() === value!.toLowerCase()
          );
        case "not_in":
          return filterValue.every(
            (v) => v.toLowerCase() !== value!.toLowerCase()
          );
        default:
          return true;
      }
    });
  });
}

function sessionHasCTA(
  session: AggregatedSessionV2,
  ctaDefinition: CTADefinition
): boolean {
  const clickedItem = getClickedItems(session.pageSessions || []);
  switch (ctaDefinition.type) {
    case "signup_submit":
      return session.signedUp;
    case "contact_submit":
      return session.contacted;
    case "cta_click":
      return ctaDefinition.eventMatchers.some((matcher) =>
        Object.keys(clickedItem).some(
          (itemId) =>
            clickedItem[itemId] &&
            itemId.toLowerCase().includes(matcher.toLowerCase())
        )
      );
    default:
      return session.signedUp || session.contacted;
  }
}

function calculatePageMetricsFromSessions(
  sessions: AggregatedSessionV2[],
  ctaDefinition: CTADefinition
): PageMetrics[] {
  const pageMap = new Map<
    string,
    {
      entrances: number;
      sessionIds: Set<string>;
      pageviews: number;
      timeOnPage: number[];
      exits: number;
      ctaClicks: number;
      assistedConversions: number;
    }
  >();

  const convertedSessionIds = new Set(
    sessions
      .filter((s) => sessionHasCTA(s, ctaDefinition))
      .map((s) => s.sessionId)
  );

  sessions.forEach((session) => {
    const pageFlow = getPageFlow(session.pageSessions || []);
    const timeOnPages = getTimeOnPages(session.pageSessions || []);
    const clickedItem = getClickedItems(session.pageSessions || []);
    const pagesInSession = new Set<string>();

    pageFlow.forEach((pagePath, index) => {
      if (!pageMap.has(pagePath)) {
        pageMap.set(pagePath, {
          entrances: 0,
          sessionIds: new Set(),
          pageviews: 0,
          timeOnPage: [],
          exits: 0,
          ctaClicks: 0,
          assistedConversions: 0,
        });
      }

      const pageData = pageMap.get(pagePath)!;

      // Count entrance (first page in session)
      if (index === 0) {
        pageData.entrances++;
      }

      // Count pageviews
      pageData.pageviews++;

      // Track unique sessions that viewed this page
      pageData.sessionIds.add(session.sessionId);

      // Count exits (last page in session)
      if (index === pageFlow.length - 1) {
        pageData.exits++;
      }

      // Track pages seen in this session for assist rate
      pagesInSession.add(pagePath);
    });

    // Add time on page data
    Object.entries(timeOnPages).forEach(([pagePath, time]) => {
      if (!pageMap.has(pagePath)) {
        pageMap.set(pagePath, {
          entrances: 0,
          sessionIds: new Set(),
          pageviews: 0,
          timeOnPage: [],
          exits: 0,
          ctaClicks: 0,
          assistedConversions: 0,
        });
      }
      pageMap.get(pagePath)!.timeOnPage.push(time);
    });

    // Count CTA clicks per page
    // Check if this session had any CTA click matching the definition
    const sessionHadCTAClick = Object.entries(clickedItem).some(
      ([itemId, clicked]) =>
        clicked &&
        ctaDefinition.eventMatchers.some((matcher) =>
          itemId.toLowerCase().includes(matcher.toLowerCase())
        )
    );

    // Attribute CTA click to the last page visited (most likely where CTA was clicked)
    // This is an approximation since we don't track which page the click occurred on
    if (sessionHadCTAClick && pageFlow.length > 0) {
      const lastPage = pageFlow[pageFlow.length - 1];
      if (pageMap.has(lastPage)) {
        pageMap.get(lastPage)!.ctaClicks++;
      }
    }

    // Calculate assisted conversions (pages seen by converting users)
    if (convertedSessionIds.has(session.sessionId)) {
      pagesInSession.forEach((pagePath) => {
        if (pageMap.has(pagePath)) {
          pageMap.get(pagePath)!.assistedConversions++;
        }
      });
    }
  });

  // Transform to PageMetrics array
  return Array.from(pageMap.entries())
    .map(([pagePath, data]) => {
      const sessions = data.sessionIds.size;
      const exits = data.exits;
      const exitRate = data.pageviews > 0 ? exits / data.pageviews : 0;
      const assistRate = sessions > 0 ? data.assistedConversions / sessions : 0;
      const ctaRate = sessions > 0 ? data.ctaClicks / sessions : 0;

      return {
        pagePath,
        entrances: data.entrances,
        sessions,
        pageviews: data.pageviews,
        medianTimeOnPage: median(data.timeOnPage),
        bounceRate: 0, // Would need more detailed tracking
        exitRate,
        samePageCTARate: ctaRate,
        assistRate,
        conversions: data.ctaClicks,
      };
    })
    .filter((p) => p.sessions > 0);
}

// PAGE ROW COMPONENT

interface PageRowProps {
  page: PageMetrics;
  totalSessions: number;
  onViewDetails: (pagePath: string) => void;
}

const PageRow: React.FC<PageRowProps> = ({ page, onViewDetails }) => {
  const lowTraffic = page.sessions < 10;

  return (
    <TableRow
      hover
      sx={{
        cursor: "pointer",
        opacity: lowTraffic ? 0.7 : 1,
      }}
      onClick={() => onViewDetails(page.pagePath)}
    >
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 300,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {page.pagePath}
          </Typography>
          {lowTraffic && (
            <Tooltip title="Low traffic - data may not be statistically significant">
              <WarningIcon fontSize="small" color="warning" />
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      <TableCell align="right">{page.entrances.toLocaleString()}</TableCell>
      <TableCell align="right">{page.sessions.toLocaleString()}</TableCell>
      <TableCell align="right">
        <Chip
          label={formatPercentage(page.samePageCTARate)}
          size="small"
          color={page.samePageCTARate > 0.1 ? "success" : "default"}
          variant="outlined"
        />
      </TableCell>
      <TableCell align="right">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Box sx={{ width: 60, mr: 1 }}>
            <LinearProgress
              variant="determinate"
              value={page.assistRate * 100}
              color={page.assistRate > 0.15 ? "success" : "primary"}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
          <Typography variant="body2">
            {formatPercentage(page.assistRate)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell align="right">
        <Typography
          variant="body2"
          color={page.exitRate > 0.7 ? "error" : "text.primary"}
        >
          {formatPercentage(page.exitRate)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        {formatDuration(page.medianTimeOnPage)}
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" onClick={() => onViewDetails(page.pagePath)}>
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

// PAGE DETAIL DIALOG

interface PageDetailDialogProps {
  open: boolean;
  pagePath: string;
  onClose: () => void;
  sessions: AggregatedSessionV2[];
  ctaDefinition: CTADefinition;
}

const PageDetailDialog: React.FC<PageDetailDialogProps> = ({
  open,
  pagePath,
  onClose,
  sessions,
  ctaDefinition,
}) => {
  // Calculate page-specific metrics from raw sessions
  const pageDetail = useMemo<PageDetailMetrics | null>(() => {
    if (!pagePath || sessions.length === 0) return null;

    // Filter sessions that viewed this page
    const pageViewingSessions = sessions.filter((s) => {
      const pageFlow = getPageFlow(s.pageSessions || []);
      return pageFlow.includes(pagePath);
    });

    if (pageViewingSessions.length === 0) return null;

    const totalPageSessions = pageViewingSessions.length;

    // Calculate time on page
    const timesOnPage = pageViewingSessions
      .map((s) => {
        const timeOnPages = getTimeOnPages(s.pageSessions || []);
        return timeOnPages[pagePath] || 0;
      })
      .filter((t) => t > 0);

    // Calculate element clicks on this page
    const elementClicks = new Map<string, number>();
    pageViewingSessions.forEach((session) => {
      const clickedItem = getClickedItems(session.pageSessions || []);
      Object.entries(clickedItem).forEach(([itemId, clicked]) => {
        if (clicked) {
          elementClicks.set(itemId, (elementClicks.get(itemId) || 0) + 1);
        }
      });
    });

    const topClickedElements = Array.from(elementClicks.entries())
      .map(([elementId, clicks]) => ({
        elementId,
        clicks,
        clickRate: clicks / totalPageSessions,
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 10);

    // Calculate element time in view
    const elementTimeInView = new Map<string, number[]>();
    pageViewingSessions.forEach((session) => {
      const timeInView = getTimeInView(session.pageSessions || []);
      Object.entries(timeInView).forEach(([elementId, time]) => {
        if (!elementTimeInView.has(elementId)) {
          elementTimeInView.set(elementId, []);
        }
        elementTimeInView.get(elementId)!.push(time);
      });
    });

    const topElementTimeInView = Array.from(elementTimeInView.entries())
      .map(([elementId, times]) => ({
        elementId,
        medianTime: median(times),
      }))
      .sort((a, b) => b.medianTime - a.medianTime)
      .slice(0, 10);

    // Calculate navigation paths (previous and next pages)
    const prevPageCounts = new Map<string, number>();
    const nextPageCounts = new Map<string, number>();

    pageViewingSessions.forEach((session) => {
      const pageFlow = getPageFlow(session.pageSessions || []);
      const pageIndex = pageFlow.indexOf(pagePath);

      if (pageIndex > 0) {
        const prevPage = pageFlow[pageIndex - 1];
        prevPageCounts.set(prevPage, (prevPageCounts.get(prevPage) || 0) + 1);
      }

      if (pageIndex >= 0 && pageIndex < pageFlow.length - 1) {
        const nextPage = pageFlow[pageIndex + 1];
        nextPageCounts.set(nextPage, (nextPageCounts.get(nextPage) || 0) + 1);
      }
    });

    const topPreviousPages = Array.from(prevPageCounts.entries())
      .map(([path, count]) => ({
        pagePath: path,
        sessions: count,
        percentage: count / totalPageSessions,
      }))
      .sort((a, b) => b.sessions - a.sessions)
      .slice(0, 5);

    const topNextPages = Array.from(nextPageCounts.entries())
      .map(([path, count]) => ({
        pagePath: path,
        sessions: count,
        percentage: count / totalPageSessions,
      }))
      .sort((a, b) => b.sessions - a.sessions)
      .slice(0, 5);

    // Calculate funnel metrics
    const sessionsWithCTAInteraction = pageViewingSessions.filter((s) => {
      const clickedItem = getClickedItems(s.pageSessions || []);
      return ctaDefinition.eventMatchers.some((matcher) =>
        Object.keys(clickedItem).some(
          (itemId) =>
            clickedItem[itemId] &&
            itemId.toLowerCase().includes(matcher.toLowerCase())
        )
      );
    }).length;

    const sessionsWithConversion = pageViewingSessions.filter((s) =>
      sessionHasCTA(s, ctaDefinition)
    ).length;

    const viewToInteraction =
      totalPageSessions > 0
        ? sessionsWithCTAInteraction / totalPageSessions
        : 0;
    const interactionToConversion =
      sessionsWithCTAInteraction > 0
        ? sessionsWithConversion / sessionsWithCTAInteraction
        : 0;

    // Calculate entrances (sessions starting on this page)
    const entrances = pageViewingSessions.filter((s) => {
      const pageFlow = getPageFlow(s.pageSessions || []);
      return pageFlow[0] === pagePath;
    }).length;

    // Calculate exit rate
    const exits = pageViewingSessions.filter((s) => {
      const pageFlow = getPageFlow(s.pageSessions || []);
      return pageFlow[pageFlow.length - 1] === pagePath;
    }).length;

    return {
      pagePath,
      entrances,
      sessions: totalPageSessions,
      pageviews: totalPageSessions, // Simplified - could track multiple views per session
      medianTimeOnPage: median(timesOnPage),
      bounceRate: 0,
      exitRate: totalPageSessions > 0 ? exits / totalPageSessions : 0,
      samePageCTARate: viewToInteraction,
      assistRate:
        totalPageSessions > 0 ? sessionsWithConversion / totalPageSessions : 0,
      conversions: sessionsWithConversion,
      topClickedElements,
      elementTimeInView: topElementTimeInView,
      topPreviousPages,
      topNextPages,
      viewToInteraction,
      interactionToConversion,
    };
  }, [pagePath, sessions, ctaDefinition]);

  if (!pageDetail) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" noWrap sx={{ maxWidth: 500 }}>
            {pagePath}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Mini Funnel */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Page Funnel
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Page Views
                  </Typography>
                  <Typography variant="h5">
                    {pageDetail.sessions.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={pageDetail.viewToInteraction * 100}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ ml: 1, minWidth: 45 }}>
                    {formatPercentage(pageDetail.viewToInteraction)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    CTA Interactions
                  </Typography>
                  <Typography variant="h5">
                    {Math.round(
                      pageDetail.sessions * pageDetail.viewToInteraction
                    ).toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={pageDetail.interactionToConversion * 100}
                      color="success"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ ml: 1, minWidth: 45 }}>
                    {formatPercentage(pageDetail.interactionToConversion)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Conversions
                  </Typography>
                  <Typography variant="h5">
                    {Math.round(
                      pageDetail.sessions *
                        pageDetail.viewToInteraction *
                        pageDetail.interactionToConversion
                    ).toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Top Clicked Elements */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Top Clicked Elements
              </Typography>
              {pageDetail.topClickedElements.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No element data available
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {pageDetail.topClickedElements.slice(0, 6).map((el) => (
                    <Box
                      key={el.elementId}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 180,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {el.elementId}
                      </Typography>
                      <Chip
                        label={formatPercentage(el.clickRate)}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>

          {/* Navigation Paths */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Navigation Paths
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                Previous Pages
              </Typography>
              {pageDetail.topPreviousPages.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  No data
                </Typography>
              ) : (
                <Stack spacing={0.5} sx={{ mb: 2 }}>
                  {pageDetail.topPreviousPages.slice(0, 3).map((p) => (
                    <Typography
                      key={p.pagePath}
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      ← {p.pagePath} ({formatPercentage(p.percentage)})
                    </Typography>
                  ))}
                </Stack>
              )}

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                Next Pages
              </Typography>
              {pageDetail.topNextPages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No data
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {pageDetail.topNextPages.slice(0, 3).map((p) => (
                    <Typography
                      key={p.pagePath}
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      → {p.pagePath} ({formatPercentage(p.percentage)})
                    </Typography>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// MAIN PAGES PAGE COMPONENT

export const PagesPage: React.FC = () => {
  const { savedSegments } = useFilters();
  const {
    sessions,
    sessionsLoading: loading,
    sessionsError: error,
  } = useDataStore();
  const [sortKey, setSortKey] = useState<SortKey>("sessions");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedPage, setSelectedPage] = useState<string | null>(null);

  // Local state for segment filter
  const [activeSegment, setActiveSegment] = useState<Segment>(
    savedSegments[0] || DEFAULT_SEGMENTS[0]
  );

  // Default CTA definition for conversion tracking
  const ctaDefinition: CTADefinition = useMemo(
    () => ({
      id: "signup",
      name: "Signup Submit",
      type: "signup_submit" as const,
      eventMatchers: ["signup", "signedUp"],
    }),
    []
  );

  // Filter sessions by active segment
  const filteredSessions = useMemo(
    () => filterSessionsBySegment(sessions, activeSegment),
    [sessions, activeSegment]
  );

  const pages = useMemo(() => {
    const transformed = calculatePageMetricsFromSessions(
      filteredSessions,
      ctaDefinition
    );

    // Sort
    return transformed.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      const multiplier = sortDirection === "desc" ? -1 : 1;
      return (aVal - bVal) * multiplier;
    });
  }, [filteredSessions, ctaDefinition, sortKey, sortDirection]);

  const totalSessions = filteredSessions.length;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "desc" ? "asc" : "desc");
    } else {
      setSortKey(key);
      setSortDirection("desc");
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Paper sx={{ mb: 3, p: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Page Effectiveness
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analyze which pages drive users toward conversion. Click a row for
              detailed page analytics.
            </Typography>
          </Box>

          {/* Segment Selector */}
          <SingleSegmentSelector
            selectedSegment={activeSegment}
            onSegmentChange={setActiveSegment}
          />
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Page Path</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortKey === "entrances"}
                  direction={sortKey === "entrances" ? sortDirection : "desc"}
                  onClick={() => handleSort("entrances")}
                >
                  Entrances
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortKey === "sessions"}
                  direction={sortKey === "sessions" ? sortDirection : "desc"}
                  onClick={() => handleSort("sessions")}
                >
                  Sessions
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortKey === "samePageCTARate"}
                  direction={
                    sortKey === "samePageCTARate" ? sortDirection : "desc"
                  }
                  onClick={() => handleSort("samePageCTARate")}
                >
                  CTA Rate
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortKey === "assistRate"}
                  direction={sortKey === "assistRate" ? sortDirection : "desc"}
                  onClick={() => handleSort("assistRate")}
                >
                  Assist Rate
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortKey === "exitRate"}
                  direction={sortKey === "exitRate" ? sortDirection : "desc"}
                  onClick={() => handleSort("exitRate")}
                >
                  Exit Rate
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortKey === "medianTimeOnPage"}
                  direction={
                    sortKey === "medianTimeOnPage" ? sortDirection : "desc"
                  }
                  onClick={() => handleSort("medianTimeOnPage")}
                >
                  Time on Page
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ width: 50 }}>
                Details
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array(8)
                  .fill(null)
                  .map((_, i) => (
                    <TableRow key={i}>
                      {Array(8)
                        .fill(null)
                        .map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton variant="text" />
                          </TableCell>
                        ))}
                    </TableRow>
                  ))
              : pages.map((page) => (
                  <PageRow
                    key={page.pagePath}
                    page={page}
                    totalSessions={totalSessions}
                    onViewDetails={setSelectedPage}
                  />
                ))}
          </TableBody>
        </Table>

        {!loading && pages.length === 0 && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              No page data available
            </Typography>
          </Box>
        )}
      </TableContainer>

      {/* Page Detail Dialog */}
      <PageDetailDialog
        open={!!selectedPage}
        pagePath={selectedPage || ""}
        onClose={() => setSelectedPage(null)}
        sessions={filteredSessions}
        ctaDefinition={ctaDefinition}
      />
    </Box>
  );
};
